import type { SkillNode, GraphNode, NodePosition, PositionsMap, LayoutResult } from './types';
import { buildGraph, choosePrimaryParent } from './graph';
import { computePackedLayout } from './layouts/packed-layout';
import { computeTreeFirstLayout } from './layouts/tree-first-layout';
import { computeFixedLayout } from './layouts/fixed-layout';
import { createPanZoom } from './pan-zoom';
import { renderNode, setupNodeHandlers, highlightNodesByType, highlightNodesByIds, highlightSelectionPath, clearHighlights, setSelectionCallback, toggleNodeSelection, clearAllSelections, getSelectedNodes, highlightSelectedEdges, setTimelineDimming, clearTimelineDimming } from './render-common';
import { buildOrthogonalPath, isExclusiveVerticalChain, getSidePoint } from './svg';
import { zoneOrder } from './config';

import skillData from '$lib/data/skill-data.json';

export type LayoutMode = 'packed' | 'tree-first' | 'wiki';

export interface UnifiedInitResult {
	zoomIn: () => void;
	zoomOut: () => void;
	resetView: () => void;
	fitToScreen: () => void;
	switchLayout: (mode: LayoutMode) => Promise<void>;
	getCurrentLayout: () => LayoutMode;
	highlightByType: (type: string) => void;
	highlightByIds: (nodeIds: string[]) => void;
	highlightPreview: (nodeId: string, suppressSelection?: boolean) => void;
	clearTypeHighlight: () => void;
	setOnNodeClick: (callback: (nodeId: string) => void) => void;
	setOnNodeHover: (callback: (nodeId: string | null) => void) => void;
	setOnSelectionChange: (callback: (selectedIds: string[]) => void) => void;
	toggleNodeSelection: (nodeId: string) => boolean;
	clearAllSelections: () => void;
	getSelectedNodes: () => string[];
	setTimelineDimming: (visibleIds: Set<string>) => void;
	clearTimelineDimming: () => void;
}

interface CachedData {
	nodes: Record<string, GraphNode>;
	packedLayout: LayoutResult;
	treeFirstLayout: LayoutResult;
	wikiLayout: LayoutResult;
}

let cachedData: CachedData | null = null;

function getCachedData(): CachedData {
	if (cachedData) return cachedData;

	const { nodes } = buildGraph(skillData as SkillNode[]);

	Object.values(nodes).forEach((node) => {
		node.highlightParentId = choosePrimaryParent(node, nodes);
		if (node.id !== 'node1') {
			node.primaryParent = choosePrimaryParent(node, nodes);
		}
	});

	const packedLayout = computePackedLayout(nodes);
	const treeFirstLayout = computeTreeFirstLayout(nodes);
	const wikiLayout = computeFixedLayout(nodes);

	cachedData = { nodes, packedLayout, treeFirstLayout, wikiLayout };
	return cachedData;
}

// FLIP animation helper
function animateFLIP(
	nodeElements: Map<string, { g: SVGGElement; currentX: number; currentY: number }>,
	oldPositions: PositionsMap,
	newPositions: PositionsMap,
	duration: number = 600
): Promise<void> {
	return new Promise((resolve) => {
		const initialRects = new Map<string, { x: number; y: number }>();
		nodeElements.forEach((data, id) => {
			const oldPos = oldPositions[id];
			if (oldPos) {
				initialRects.set(id, { x: oldPos.x, y: oldPos.y });
			}
		});

		nodeElements.forEach((data, id) => {
			const newPos = newPositions[id];
			const oldPos = initialRects.get(id);
			
			if (newPos && oldPos) {
				const dx = oldPos.x - newPos.x;
				const dy = oldPos.y - newPos.y;
				data.g.setAttribute('transform', `translate(${newPos.x + dx}, ${newPos.y + dy})`);
				data.currentX = newPos.x;
				data.currentY = newPos.y;
			}
		});

		document.body.offsetHeight;

		let completed = 0;
		const total = nodeElements.size;
		
		nodeElements.forEach((data, id) => {
			const newPos = newPositions[id];
			const oldPos = initialRects.get(id);
			
			if (newPos && oldPos) {
				const el = data.g;
				el.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
				el.setAttribute('transform', `translate(${newPos.x}, ${newPos.y})`);
				
				el.addEventListener('transitionend', () => {
					completed++;
					if (completed >= total) {
						resolve();
					}
				}, { once: true });
			} else {
				completed++;
			}
		});

		setTimeout(() => {
			if (completed < total) {
				resolve();
			}
		}, duration + 100);
	});
}

export function initUnifiedTree(
	container: HTMLElement,
	svg: SVGSVGElement,
	tooltip: HTMLElement,
	zoomInfo: HTMLElement,
	initialMode: LayoutMode = 'packed'
): UnifiedInitResult {
	const { nodes, packedLayout, treeFirstLayout, wikiLayout } = getCachedData();

	let currentMode = initialMode;
	let currentLayout: LayoutResult;
	if (initialMode === 'packed') {
		currentLayout = packedLayout;
	} else if (initialMode === 'tree-first') {
		currentLayout = treeFirstLayout;
	} else {
		currentLayout = wikiLayout;
	}

	// Setup SVG
	svg.setAttribute('width', String(currentLayout.totalWidth));
	svg.setAttribute('height', String(currentLayout.totalHeight));

	// Add SVG defs for selection glow filter
	const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
	defs.innerHTML = `
		<filter id="selectionGlow" x="-50%" y="-50%" width="200%" height="200%">
			<feGaussianBlur stdDeviation="3" result="coloredBlur"/>
			<feMerge>
				<feMergeNode in="coloredBlur"/>
				<feMergeNode in="SourceGraphic"/>
			</feMerge>
		</filter>
	`;
	svg.appendChild(defs);

	const mainGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
	mainGroup.id = 'main-group';
	svg.appendChild(mainGroup);

	// Create separate groups for proper z-order (back to front: static -> edges -> nodes)
	const staticGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
	staticGroup.id = 'static-group';
	mainGroup.appendChild(staticGroup);

	const edgesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
	edgesGroup.id = 'edges-group';
	mainGroup.appendChild(edgesGroup);

	const nodesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
	nodesGroup.id = 'nodes-group';
	mainGroup.appendChild(nodesGroup);

	// Track elements using the proper types
	let nodeElements = new Map<string, { g: SVGGElement; currentX: number; currentY: number }>();
	let nodeElementsForHandlers: Record<string, SVGGElement> = {};
	let edgeElementsForHandlers: Record<string, SVGPathElement> = {};
	let edgeElements: SVGPathElement[] = [];
	let zoneLabels: SVGTextElement[] = [];
	let zoneDividers: SVGLineElement[] = [];
	let levelLines: SVGLineElement[] = [];
	let levelLabels: SVGTextElement[] = [];

	// Callbacks
	let onExternalNodeClick: ((nodeId: string) => void) | undefined = undefined;
	let onExternalNodeHover: ((nodeId: string | null) => void) | undefined = undefined;

	function clearElements(): void {
		// Clear static group
		while (staticGroup.firstChild) {
			staticGroup.removeChild(staticGroup.firstChild);
		}
		// Clear edges group
		while (edgesGroup.firstChild) {
			edgesGroup.removeChild(edgesGroup.firstChild);
		}
		// Clear nodes group
		while (nodesGroup.firstChild) {
			nodesGroup.removeChild(nodesGroup.firstChild);
		}
		nodeElements.clear();
		nodeElementsForHandlers = {};
		edgeElementsForHandlers = {};
		edgeElements = [];
		zoneLabels = [];
		zoneDividers = [];
		levelLines = [];
		levelLabels = [];
	}

	function renderStaticElements(layout: LayoutResult, mode: LayoutMode, targetGroup: SVGGElement = staticGroup): void {
		// Skip zone elements for wiki layout (it doesn't have zones)
		if (mode === 'wiki') return;

		const zoneLabelY = layout.positions['node1'].y + 24 + 30;
		const zoneGap = mode === 'packed' ? 76 : 56;

		zoneOrder.forEach((z, i) => {
			const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
			label.setAttribute('x', String(layout.zoneCenters[z]));
			label.setAttribute('y', String(zoneLabelY));
			label.setAttribute('class', `zone-label zone-${z}`);
			label.textContent = z;
			targetGroup.appendChild(label);
			zoneLabels.push(label);

			if (i < zoneOrder.length - 1) {
				const dividerX = layout.zoneCenters[z] + layout.zoneWidths[z] / 2 + zoneGap / 2;
				const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
				line.setAttribute('x1', String(dividerX));
				line.setAttribute('y1', String(zoneLabelY + 12));
				line.setAttribute('x2', String(dividerX));
				line.setAttribute('y2', String(layout.totalHeight - 50));
				line.setAttribute('class', 'zone-line');
				targetGroup.appendChild(line);
				zoneDividers.push(line);
			}
		});

		for (let level = 1; level <= layout.maxLevel; level++) {
			const y = layout.levelY[level];
			
			const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
			line.setAttribute('x1', String(60));
			line.setAttribute('y1', String(y));
			line.setAttribute('x2', String(layout.totalWidth - 60));
			line.setAttribute('y2', String(y));
			line.setAttribute('class', 'level-line');
			targetGroup.appendChild(line);
			levelLines.push(line);

			const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
			text.setAttribute('x', String(50));
			text.setAttribute('y', String(y + 4));
			text.setAttribute('class', 'level-label');
			text.textContent = String(level);
			targetGroup.appendChild(text);
			levelLabels.push(text);
		}
	}

	function renderEdges(layout: LayoutResult, mode: LayoutMode, targetGroup: SVGGElement = edgesGroup): void {
		const transitionChannels: Record<string, number> = {};
		for (let level = 0; level < layout.maxLevel; level++) {
			transitionChannels[`${level}-${level + 1}`] = (layout.levelY[level] + layout.levelY[level + 1]) / 2;
		}

		Object.values(nodes).forEach((node) => {
			if (!layout.positions[node.id]) return;

			node.children.forEach((childId) => {
				if (!layout.positions[childId]) return;
				const child = nodes[childId];

				const previousLevelParents = child.parents.filter(
					(parentId) => nodes[parentId].level === child.level - 1
				);
				const nextLevelChildren = node.children.filter(
					(grandchildId) => nodes[grandchildId].level === node.level + 1
				);

				let isTreeEdge = false;
				let preferStraight = false;

				if (mode === 'tree-first') {
					isTreeEdge = child.primaryParent === node.id;
					preferStraight =
						isTreeEdge &&
						previousLevelParents.length === 1 &&
						previousLevelParents[0] === node.id &&
						nextLevelChildren.length === 1 &&
						nextLevelChildren[0] === childId;
				} else {
					preferStraight = isExclusiveVerticalChain(node.id, childId, nodes);
				}

				const startPos = layout.positions[node.id];
				const endPos = layout.positions[childId];

				let d: string;
				
				if (mode === 'wiki') {
					// For wiki layout: straight lines from center to center
					d = `M ${startPos.x} ${startPos.y} L ${endPos.x} ${endPos.y}`;
				} else {
					const startPoint = getSidePoint(startPos, 'bottom');
					const endPoint = getSidePoint(endPos, 'top');
					
					if (preferStraight && Math.abs(startPoint.x - endPoint.x) < 0.75) {
						d = `M ${startPoint.x} ${startPoint.y} L ${endPoint.x} ${endPoint.y}`;
					} else {
						const transitionKey = `${node.level}-${child.level}`;
						const midY = transitionChannels[transitionKey] ?? (startPoint.y + endPoint.y) / 2;
						d = `M ${startPoint.x} ${startPoint.y} L ${startPoint.x} ${midY} L ${endPoint.x} ${midY} L ${endPoint.x} ${endPoint.y}`;
					}
				}

				const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
				path.setAttribute('d', d);
				path.setAttribute('class', mode === 'tree-first' ? (isTreeEdge ? 'tree-connection' : 'secondary-connection') : 'connection');
				path.setAttribute('opacity', '0');
				targetGroup.appendChild(path);
				
				const edgeKey = `${node.id}|${childId}`;
				edgeElementsForHandlers[edgeKey] = path;
				edgeElements.push(path);
			});

			// For wiki mode: render same-level connections with a distinct style
			if (mode === 'wiki' && node.sameLevelNeighbors) {
				node.sameLevelNeighbors.forEach((neighborId) => {
					// Only draw each same-level edge once (when node.id < neighborId)
					if (node.id >= neighborId) return;
					if (!layout.positions[neighborId]) return;

					const startPos = layout.positions[node.id];
					const endPos = layout.positions[neighborId];
					const d = `M ${startPos.x} ${startPos.y} L ${endPos.x} ${endPos.y}`;

					const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
					path.setAttribute('d', d);
					path.setAttribute('class', 'same-level-connection');
					path.setAttribute('opacity', '0');
					targetGroup.appendChild(path);

					const edgeKey = `${node.id}|${neighborId}`;
					edgeElementsForHandlers[edgeKey] = path;
					edgeElements.push(path);
				});
			}
		});
	}

	function renderNodes(layout: LayoutResult, mode: LayoutMode, targetGroup: SVGGElement = nodesGroup): void {
		Object.values(layout.positions).forEach((pos) => {
			// Get icon paths from node data (now included in skill-data.json)
			const iconDefault = pos.node.iconDefault;
			const iconActive = pos.node.iconActive;
			// Always use active icon for initial render (both parameters are active now)
			const g = renderNode(pos, iconActive, iconActive, targetGroup);
			
			nodeElements.set(pos.node.id, {
				g,
				currentX: pos.x,
				currentY: pos.y
			});
			
			nodeElementsForHandlers[pos.node.id] = g;
			
			// Use active icon for tooltip if available, otherwise default
			const tooltipIcon = iconActive || iconDefault;
			
			setupNodeHandlers(
				g, 
				pos.node, 
				tooltipIcon, 
				tooltip, 
				nodes, 
				nodeElementsForHandlers, 
				edgeElementsForHandlers, 
				mode === 'tree-first',
				(node) => {
					// Toggle selection when clicked - pass nodes for path auto-selection and edgeElements for highlighting
					toggleNodeSelection(node.id, nodeElementsForHandlers, nodes, edgeElementsForHandlers);
					// Notify external callback
					onExternalNodeClick?.(node.id);
				},
				(nodeId) => onExternalNodeHover?.(nodeId)
			);
		});
	}

	function fadeInEdges(): Promise<void> {
		return new Promise((resolve) => {
			edgeElements.forEach((el, i) => {
				setTimeout(() => {
					el.style.transition = 'opacity 400ms ease';
					const isTree = el.classList.contains('tree-connection');
					const isSecondary = el.classList.contains('secondary-connection');
				el.setAttribute('opacity', isTree ? '0.55' : isSecondary ? '0.55' : '0.55');
				}, i * 3);
			});
			setTimeout(resolve, 500);
		});
	}

	function getLayoutForMode(mode: LayoutMode): LayoutResult {
		switch (mode) {
			case 'packed': return packedLayout;
			case 'tree-first': return treeFirstLayout;
			case 'wiki': return wikiLayout;
		}
	}

	function initialRender(mode: LayoutMode): void {
		clearElements();
		currentLayout = getLayoutForMode(mode);
		currentMode = mode;
		
		svg.setAttribute('width', String(currentLayout.totalWidth));
		svg.setAttribute('height', String(currentLayout.totalHeight));
		
		renderStaticElements(currentLayout, mode);
		renderEdges(currentLayout, mode);
		renderNodes(currentLayout, mode);
	}

	// Initial render
	initialRender(initialMode);

	// Setup pan/zoom
	const panZoom = createPanZoom(container, svg, mainGroup, zoomInfo);

	// Initial fit
	setTimeout(() => {
		panZoom.fitToScreen(currentLayout.totalWidth, currentLayout.totalHeight);
		fadeInEdges();
	}, 100);

	async function switchLayout(newMode: LayoutMode): Promise<void> {
		const oldLayout = currentLayout;
		const newLayout = getLayoutForMode(newMode);

		svg.setAttribute('width', String(Math.max(oldLayout.totalWidth, newLayout.totalWidth)));
		svg.setAttribute('height', String(Math.max(oldLayout.totalHeight, newLayout.totalHeight)));

		// Fade out edges quickly
		edgeElements.forEach(el => {
			el.style.transition = 'opacity 100ms ease';
			el.setAttribute('opacity', '0');
		});

		await new Promise(r => setTimeout(r, 100));

		// Remove old edges
		edgeElements.forEach(el => el.remove());
		
		// Clear static elements group
		while (staticGroup.firstChild) {
			staticGroup.removeChild(staticGroup.firstChild);
		}
		zoneLabels = [];
		zoneDividers = [];
		levelLines = [];
		levelLabels = [];
		
		// Clear edge tracking
		edgeElements = [];
		Object.keys(edgeElementsForHandlers).forEach(key => delete edgeElementsForHandlers[key]);

		// Render new static elements and edges
		renderStaticElements(newLayout, newMode, staticGroup);
		renderEdges(newLayout, newMode, edgesGroup);

		// Store old positions for FLIP
		const oldPositions = { ...oldLayout.positions };

		// Animate nodes to new positions (100ms fast transition)
		await animateFLIP(nodeElements, oldPositions, newLayout.positions, 100);

		// Move animated nodes from old positions to nodes group if needed
		// (ensure they're in the correct group after animation)
		nodeElements.forEach((data) => {
			if (data.g.parentNode !== nodesGroup) {
				nodesGroup.appendChild(data.g);
			}
		});

		// Update handler references after animation
		nodeElementsForHandlers = {};
		nodeElements.forEach((data, id) => {
			nodeElementsForHandlers[id] = data.g;
		});

		// Fade in new edges quickly
		await fadeInEdgesFast();

		// Re-apply selection highlighting to edges between selected nodes
		const selectedNodeIds = getSelectedNodes();
		if (selectedNodeIds.length > 0) {
			highlightSelectedEdges(new Set(selectedNodeIds), edgeElementsForHandlers);
		}

		currentLayout = newLayout;
		currentMode = newMode;
	}

	function fadeInEdgesFast(): Promise<void> {
		return new Promise((resolve) => {
			edgeElements.forEach((el) => {
				el.style.transition = 'opacity 100ms ease';
				const isTree = el.classList.contains('tree-connection');
				const isSecondary = el.classList.contains('secondary-connection');
				el.setAttribute('opacity', isTree ? '0.55' : isSecondary ? '0.55' : '0.55');
			});
			setTimeout(() => {
				resolve();
			}, 100);
		});
	}

	function getCurrentLayout(): LayoutMode {
		return currentMode;
	}

	function highlightByType(type: string): void {
		highlightNodesByType(type, nodes, nodeElementsForHandlers, edgeElementsForHandlers);
	}

	function clearTypeHighlight(): void {
		clearHighlights(nodeElementsForHandlers, edgeElementsForHandlers);
	}

	function setOnNodeClick(callback: (nodeId: string) => void): void {
		onExternalNodeClick = callback;
	}

	function setOnNodeHover(callback: (nodeId: string | null) => void): void {
		onExternalNodeHover = callback;
	}

	function setOnSelectionChange(callback: (selectedIds: string[]) => void): void {
		setSelectionCallback(callback);
	}

	return {
		zoomIn: panZoom.zoomIn,
		zoomOut: panZoom.zoomOut,
		resetView: panZoom.resetView,
		fitToScreen: () => panZoom.fitToScreen(currentLayout.totalWidth, currentLayout.totalHeight),
		switchLayout,
		getCurrentLayout,
		highlightByType,
		highlightByIds: (nodeIds: string[]) => highlightNodesByIds(nodeIds, nodes, nodeElementsForHandlers, edgeElementsForHandlers),
		highlightPreview: (nodeId: string, suppressSelection: boolean = false) => {
			const node = nodes[nodeId];
			if (node) highlightSelectionPath(node, nodes, nodeElementsForHandlers, edgeElementsForHandlers, suppressSelection);
		},
		clearTypeHighlight,
		setOnNodeClick,
		setOnNodeHover,
		setOnSelectionChange,
		toggleNodeSelection: (nodeId: string) => toggleNodeSelection(nodeId, nodeElementsForHandlers, nodes, edgeElementsForHandlers),
		clearAllSelections: () => clearAllSelections(nodeElementsForHandlers, edgeElementsForHandlers),
		getSelectedNodes,
		setTimelineDimming: (visibleIds: Set<string>) => setTimelineDimming(nodeElementsForHandlers, visibleIds, edgeElementsForHandlers),
		clearTimelineDimming: () => clearTimelineDimming(nodeElementsForHandlers, edgeElementsForHandlers)
	};
}
